import { useMatches } from '@remix-run/react';
import { useEffect } from 'react';
import type { PageType } from 'types';

import useEnv from '~/hooks/useEnv';
import useRegion from '~/hooks/useRegion';
import useUser from '~/hooks/useUser';
import gtm from '~/services/gtm.client';

function getGoogleTagManagerConfig(
  region: string,
  env: ReturnType<typeof useEnv>
) {
  const validRegion = ['GB', 'DE'].includes(region) ? region : 'GB';

  return {
    containerId: env[`GTM_CONTAINER_ID_${validRegion}` as keyof typeof env],
    gtmAuth: `&gtm_auth=${env[`GTM_AUTH_${validRegion}` as keyof typeof env]}`,
    gtmPreview: `&gtm_preview=${env[`GTM_PREVIEW_${validRegion}` as keyof typeof env]}`
  };
}

const PAGE_TYPES: Record<string, PageType> = {
  '_mm.$productType.$brand.$subBrand.$slug': 'pdp',
  '_mm.($gender).release-dates.($brand).($subBrand)': 'plp',
  '_mm.($gender).sole-market.($brand).($subBrand)': 'sole-market',
  '_mm._index': 'homepage',
  '_mm.contact': 'contact',
  '_mm.news.$slug': 'news-article',
  '_mm.profile.notifications': 'profile-notifications',
  '_mm.sale': 'sales event generic',
  '_sam.s.$affiliate.$(slug)': 'single-affiliate-pdp',
  '_smm.marketplace.buy.listings.$listing_id.payment': 'sole-market-payment',
  '_smm.marketplace.buy.products.$productId.(sizes).($size)':
    'sole-market-listing-selection',
  '_smm.sole-market.$brand.$subBrand.$slug': 'sole-market-pdp'
};

function getPageType(matches: ReturnType<typeof useMatches>) {
  const match = matches[matches.length - 1];
  const routeId = match?.id?.split('routes/').pop();
  const pageType = PAGE_TYPES[routeId ?? ''];

  return pageType ?? routeId ?? 'unknown';
}

const GoogleTagManager = () => {
  const env = useEnv();
  const user = useUser();
  const matches = useMatches();
  const region = useRegion();

  const { gtmAuth, gtmPreview, containerId } = getGoogleTagManagerConfig(
    region.code,
    env
  );

  useEffect(() => {
    const gtmScript = document.createElement('script');

    gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl+'${gtmAuth}${gtmPreview}&gtm_cookies_win=x';f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','${containerId}');
  window.dataLayer = window.dataLayer || [];`;

    document.head.appendChild(gtmScript);
  }, [containerId, gtmAuth, gtmPreview]);

  useEffect(() => {
    gtm.addDefaultDataToLayer({
      pageType: getPageType(matches),
      user
    });

    gtm.loadPixels();
  }, [matches, user]);

  return (
    <noscript>
      <iframe
        title="google tag manager"
        src={`https://www.googletagmanager.com/ns.html?id=${containerId}`}
        height="0"
        width="0"
        style={{ display: 'none', visibility: 'hidden' }}
      />
    </noscript>
  );
};

export default GoogleTagManager;
